/**
 * Reed.ai Design System — Banner
 *
 * Variants: verification (default) | info | warning | error
 * Sizes:    desktop (56px, full-width) | mobile (auto-height)
 *
 * Source: Figma "Reed.ai - Global components" — Banners page
 * Node:   2060:1434 (VerificationBanner/Desktop)
 *
 * Design details:
 *  VerificationBanner — bg #00E6A6 (hyde-park-green), 56px height
 *  Layout: info icon (24px) + body text + pill button + close icon button (right-absolute)
 *  Text   — Red Hat Text Medium 14px, piccadilly-blue #081351
 *  Button — Tertiary / Mobile style: border #081351, 100px radius, px-[16px] py-[8px]
 *  Close  — absolute right-[8px] top-[8px], 40px tap area, rounded-full
 */

import React from 'react';
import { cn } from '../../utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BannerVariant = 'verification' | 'info' | 'warning' | 'error';
export type BannerSize    = 'desktop' | 'mobile';

export interface BannerProps {
  /** Visual style / colour */
  variant?: BannerVariant;
  /** Main text content */
  message: string;
  /** Label for the optional CTA button */
  actionLabel?: string;
  /** Called when the CTA button is clicked */
  onAction?: () => void;
  /** Called when the dismiss (×) button is clicked */
  onDismiss?: () => void;
  /** Whether to show the dismiss button */
  dismissible?: boolean;
  size?: BannerSize;
  className?: string;
}

// ─── Colour map ───────────────────────────────────────────────────────────────

const VARIANT_BG: Record<BannerVariant, string> = {
  verification: 'bg-[#00E6A6]',   // hyde-park-green
  info:         'bg-[#DBEAFE]',   // blue-100
  warning:      'bg-[#FEF3C7]',   // amber-100
  error:        'bg-[#FEE2E2]',   // red-100
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function InfoIcon() {
  return (
    <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#081351" strokeWidth="1.5" />
      <path d="M12 11v5" stroke="#081351" strokeLinecap="round" strokeWidth="1.5" />
      <circle cx="12" cy="8" fill="#081351" r="0.75" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6 6 18M6 6l12 12" stroke="#081351" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#92400E" strokeWidth="1.5" />
      <path d="M12 9v4" stroke="#92400E" strokeLinecap="round" strokeWidth="1.5" />
      <circle cx="12" cy="16.5" fill="#92400E" r="0.75" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#991B1B" strokeWidth="1.5" />
      <path d="M15 9l-6 6M9 9l6 6" stroke="#991B1B" strokeLinecap="round" strokeWidth="1.5" />
    </svg>
  );
}

const VARIANT_ICON: Record<BannerVariant, React.ReactNode> = {
  verification: <InfoIcon />,
  info:         <InfoIcon />,
  warning:      <WarningIcon />,
  error:        <ErrorIcon />,
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Reed.ai Banner
 *
 * @example
 * // Verification banner (full-width, green)
 * <Banner
 *   variant="verification"
 *   message="Verify your account to unlock your free trial"
 *   actionLabel="Verify now"
 *   onAction={() => router.push('/verify')}
 *   dismissible
 *   onDismiss={() => setBannerVisible(false)}
 * />
 *
 * // Warning banner (no action)
 * <Banner variant="warning" message="Your subscription expires in 3 days." dismissible onDismiss={close} />
 */
export function Banner({
  variant = 'verification',
  message,
  actionLabel,
  onAction,
  onDismiss,
  dismissible = false,
  size = 'desktop',
  className,
}: BannerProps) {
  const isDesktop = size === 'desktop';

  return (
    <div
      className={cn(
        'relative flex items-center justify-center w-full',
        'px-6 py-2',
        isDesktop ? 'h-14' : 'min-h-[48px] py-3',
        VARIANT_BG[variant],
        className,
      )}
      role="status"
    >
      {/* Content centred row */}
      <div className="flex flex-1 items-center justify-center gap-4 min-w-0">
        {/* Icon + message */}
        <div className="flex items-center gap-2 shrink-0">
          {VARIANT_ICON[variant]}
          <p
            className={cn(
              'font-body font-medium leading-[1.5] text-[#081351]',
              isDesktop ? 'text-[14px]' : 'text-[13px]',
              'whitespace-nowrap',
            )}
          >
            {message}
          </p>
        </div>

        {/* Optional CTA button */}
        {actionLabel && (
          <button
            className={cn(
              'shrink-0 border border-[#081351] rounded-[100px]',
              'px-4 py-2 font-body font-medium text-[14px] text-[#081351] leading-[1.5]',
              'transition-colors hover:bg-[rgba(8,19,81,0.08)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#081351] focus-visible:ring-offset-2',
            )}
            type="button"
            onClick={onAction}
          >
            {actionLabel}
          </button>
        )}
      </div>

      {/* Dismiss button — absolutely positioned right */}
      {dismissible && (
        <button
          aria-label="Dismiss"
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2',
            'flex items-center justify-center size-10 rounded-full',
            'transition-colors hover:bg-[rgba(8,19,81,0.08)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#081351]',
          )}
          type="button"
          onClick={onDismiss}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
}

export default Banner;
